import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { PersonalEvent as Event } from '../../../../types/event';
import * as eventService from '../../../../services/eventService';
import AgendaCalendarView from './AgendaCalendarView';
import AgendaListView from './AgendaListView';
import BirthdaysTab from './BirthdaysView';
import EventDetailModal from '../../../../components/modals/EventDetailModal';

type TabType = 'calendar' | 'list' | 'birthdays';

export default function AgendaPage() {
  const { t } = useTranslation();
  const [events, setEvents] = useState<Event[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('calendar');
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 640 : false,
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const handler = (e: MediaQueryListEvent) => setIsSmallScreen(!e.matches);
    setIsSmallScreen(!mq.matches);

    const hasAddEventListener = typeof (mq as any).addEventListener === 'function';
    const hasAddListener = typeof (mq as any).addListener === 'function';

    if (hasAddEventListener) {
      (mq as any).addEventListener('change', handler);
    } else if (hasAddListener) {
      (mq as any).addListener(handler);
    }

    return () => {
      if (hasAddEventListener) {
        (mq as any).removeEventListener('change', handler);
      } else if (typeof (mq as any).removeListener === 'function') {
        (mq as any).removeListener(handler);
      }
    };
  }, []);

  useEffect(() => {
    if (isSmallScreen && activeTab === 'calendar') {
      setActiveTab('list');
    }
  }, [isSmallScreen, activeTab]);

  const fetchEvents = () => {
    setLoading(true);
    eventService
      .getMyEvents()
      .then(setEvents)
      .catch((error) => {
        console.error('Failed to fetch events:', error);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const location = useLocation();

  useEffect(() => {
    const state: any = (location && (location.state as any)) || {};
    if (state && state.openEventId) {
      setSelectedEventId(Number(state.openEventId));
      setIsModalOpen(true);
    }
  }, [location.state]);

  const tabs: { key: TabType; label: string }[] = [
    { key: 'calendar', label: t('general.calendar') },
    { key: 'list', label: t('general.list') },
    { key: 'birthdays', label: t('general.birthdays') || 'Birthdays' },
  ];

  const handleEventClick = (eventId: number) => {
    setSelectedEventId(eventId);
    setIsModalOpen(true);
  };

  return (
    <div className="flex min-h-screen flex-col p-4">
      {/* Header */}
      <div className="flex items-center justify-between md:mb-8">
        <div>
          <h1 className="mb-2 text-2xl font-bold text-primary sm:text-3xl">{t('general.agenda')}</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-4 border-b border-details/20">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`relative px-4 pb-3 pt-2 font-medium transition-all duration-150 ${
              tab.key === 'calendar' ? 'hidden sm:inline-flex' : ''
            } ${activeTab === tab.key ? 'border-b-2 border-primary text-primary' : 'text-details/70 hover:text-primary'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1">
        {activeTab === 'calendar' ? (
          <AgendaCalendarView events={events} loading={loading} onEventClick={handleEventClick} />
        ) : activeTab === 'list' ? (
          <AgendaListView events={events} loading={loading} />
        ) : (
          <BirthdaysTab />
        )}
      </div>

      {/* Event Detail Modal */}
      <EventDetailModal open={isModalOpen} setOpen={setIsModalOpen} eventId={selectedEventId} />
    </div>
  );
}
